import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../types';
import { sampleBrands } from '../data/sampleData';
import NewProjectModal from './NewProjectModal';
import EditProjectModal from './EditProjectModal';
import { useProjects } from '../contexts/ProjectContext';

// Color palette aligned with design system
const PROJECT_COLORS = [
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#eab308', // yellow-500
  '#ec4899', // pink-500
  '#22c55e', // green-500
  '#0ea5e9', // accent-500 (light blue)
  '#f97316', // orange-500 (repeat for more projects)
  '#14b8a6', // teal-500
];

// Generate a consistent color for a project based on its ID
const getProjectColor = (projectId: string): string => {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = ((hash << 5) - hash) + projectId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  const colorIndex = Math.abs(hash) % PROJECT_COLORS.length;
  return PROJECT_COLORS[colorIndex];
};

const ProjectList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<Project | null>(null);
  const { projects, addProject, updateProject, deleteProject } = useProjects();

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrand === 'all' || project.brand.id === selectedBrand;
      
      return matchesSearch && matchesBrand;
    });
  }, [projects, searchTerm, selectedBrand]);

  const handleProjectCreated = (newProject: Project) => {
    addProject(newProject);
  };

  const handleDeleteClick = (project: Project) => {
    setDeleteConfirmProject(project);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmProject) {
      deleteProject(deleteConfirmProject.id);
      setDeleteConfirmProject(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmProject(null);
  };

  const handleEditClick = (project: Project) => {
    setEditProject(project);
  };

  const handleEditClose = () => {
    setEditProject(null);
  };

  const handleProjectUpdated = (updatedProject: Project) => {
    // Extract only the fields that should be updated (excluding id, createdAt)
    const { id, createdAt, ...updates } = updatedProject;
    updateProject(id, updates);
    setEditProject(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-primary-900">Projects</h2>
          <p className="text-gray-600 mt-1">Manage and evaluate keyword selections for your product lists</p>
        </div>
        <button 
          onClick={() => setShowNewProjectModal(true)}
          className="btn-primary mt-4 sm:mt-0"
        >
          + New Project
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none"
              style={{ 
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
            >
              <option value="all">All Brands</option>
              {sampleBrands.map(brand => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map(project => (
          <div key={project.id} className="card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: getProjectColor(project.id) }}
                >
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900">{project.name}</h3>
                  <p className="text-sm text-gray-600">{project.brand.name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(project);
                  }}
                  type="button"
                  className="text-gray-400 hover:text-accent-600 transition-colors p-1"
                  title="Edit project"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(project);
                  }}
                  type="button"
                  className="text-gray-400 hover:text-red-600 transition-colors p-1"
                  title="Delete project"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4">{project.description}</p>

            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-primary-900">{project.products.length}</span>
                <span> {project.products.length === 1 ? 'product' : 'products'}</span>
              </div>
              <Link
                to={`/project/${project.id}`}
                className="btn-primary text-sm"
              >
                Evaluate
              </Link>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-primary-900 mb-2">No projects found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <NewProjectModal
          onClose={() => setShowNewProjectModal(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {/* Edit Project Modal */}
      {editProject && (
        <EditProjectModal
          project={editProject}
          onClose={handleEditClose}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleDeleteCancel}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-primary-900 mb-4">Delete Project</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold text-primary-900">"{deleteConfirmProject.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
